import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import Layout from './ui/components/layout/Layout';
import Home from './pages/home/Home';
import ProtectedRoute from './ProtectedRoute';
import UserDashboardLayout from './ui/components/layout/UserDashboardLayout';

import {
  RealEstateHome,
  SingleProductPage,
  ConsumerGoodsHome,
  HomeLivingHome,
  ElectronicsHome,
  SalesHome,
  FoodAndDrinkHome,
  CartItems,
  CheckoutItems,
  CheckoutPaymentSuccess,
  UserDashboard,
  ShoppingOverview,
  DirectDebit,
  Favorites,
  RecentlyViewed,
  CheckoutPaymentFailed,
  StaySmallSmallBookingReceiptSuccess,
  ExtendStaySuccessReceipt,
  ModifyStaySuccessReceipt,
  ModifyStayRefundSuccessReceipt,
  CancelStaySuccessReceipt,
  StaySmallSmallSummary,
  StaySmallSmallStay,
  StaySmallSmallFavorites,
  StaySmallSmallRecentlyViewed,
  StaySmallSmallProfile,
  StaySmallSmallSinglePropertyPage,
  RentSmallSmallSinglePropertyPage,
  RentSmallSmallAllProperties,
  RentSmallSmallLanding,
  RentSmallSmallLandingPrime,
  StaySmallSmallBookingInitiatedSuccess,
  // Notifications,
  // CreditWallet,
  RentSmallSmallProfile,
  RentSmallSmallSummary,
  RentSmallSmallInspections,
  RentSmallSmallRental,
  RentSmallSmallRentalVerification,
  RentSmallSmallRecentlyViewed,
  RentSmallSmallFavorites,
  SubscriptionTerms,
  SignUp,
  Login,
  VerificationSent,
  RentSmallSmallResults,
  Summary,
  Purchases,
  ForgotPassword,
  ResetPassword,
  ProfileSummary,
  AccountVerification,
  DeliveryAddress,
  Feedback,
  ResolutionCentre,
  UserAccountProfile,
  TermsOfUse,
  HowItWorks,
  BuyerAndVendorProtection,
  MarketplaceGuidelines,
  RefundsAndReturns,
  BuyingGuide,
  DirectDebitSetup,
  DirectDebitInitialized,
  SearchProductResult,
  AboutUs,
  VerifyEmail,
  CompleteBooking,
  PrivacyPolicy,
  VendorAllProduct,
  ExtendStayPayment,
  BookingTerms,
  SubscriberVerificationPage,
  RSSSubscriberVerificationSuccessPage,
  ReturningCustomerVerificationPage,
  RSSPaymentSetupPage,
  RSSPaymentSuccessPage,
  PrimeVerificationPage,
  PrimeVerificationSuccessPage,
  PrimeReturningCustomerVerificationPage,
} from './lazyImports';

import CartItemsSkeleton from './ui/components/skeletons/CartItemsSkeleton';
import CheckoutItemsSkeleton from './ui/components/skeletons/CheckoutItemsSkeleton';
import ProductDetailsSkeleton from './ui/components/skeletons/ProductDetailsSkeleton';
import CheckoutPaymentSkeleton from './ui/components/skeletons/CheckoutPaymentSkeleton';
import DashboardSummarySkeleton from './ui/components/skeletons/DashboardSummarySkeleton';
import LandingPageSkeleton from './ui/components/skeletons/LandingPageSkeleton';
import DynamicSubCategoryPage from './pages/DynamicSubcategoryPage';
import PageNotFound from './pages/pageNotFound/PageNotFound';

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route
          path="home-living"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <HomeLivingHome />
            </Suspense>
          }
        />

        <Route
          path="electronics"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <ElectronicsHome />
            </Suspense>
          }
        />
        <Route
          path="sales-offers"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <SalesHome />
            </Suspense>
          }
        />

        <Route
          path="search-results"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <SearchProductResult />
            </Suspense>
          }
        />

        <Route
          path="real-estate"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RealEstateHome />
            </Suspense>
          }
        />

        <Route
          path="real-estate/rent-smallsmall"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RentSmallSmallLanding />
            </Suspense>
          }
        />

        <Route
          path="real-estate/rent-smallsmall/results"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RentSmallSmallResults />
            </Suspense>
          }
        />

        <Route
          path="real-estate/rent-smallsmall/all-properties"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RentSmallSmallAllProperties />
            </Suspense>
          }
        />

        <Route
          path="real-estate/rent-smallsmall/prime"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RentSmallSmallLandingPrime />
            </Suspense>
          }
        />

        <Route
          path="real-estate/rent-smallsmall/:id/:name"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RentSmallSmallSinglePropertyPage />
            </Suspense>
          }
        />

        <Route
          path=":category/:sub_category"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <DynamicSubCategoryPage />
            </Suspense>
          }
        />

        <Route
          path="food-drink"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <FoodAndDrinkHome />
            </Suspense>
          }
        />

        <Route
          path="lifestyle-consumer-goods"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <ConsumerGoodsHome />
            </Suspense>
          }
        />

        <Route
          path=":category/:sub_category/:productID/:slug"
          element={
            <Suspense
              fallback={
                <ProductDetailsSkeleton showAside showRecommendations />
              }
            >
              <SingleProductPage />
            </Suspense>
          }
        />

        <Route
          path=":real-estate/stay-smallsmall/:id/:name"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <StaySmallSmallSinglePropertyPage />
            </Suspense>
          }
        />

        <Route
          path="terms-of-use"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <TermsOfUse />
            </Suspense>
          }
        />

        <Route
          path="privacy-policy"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />

        <Route
          path="how-it-works"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <HowItWorks />
            </Suspense>
          }
        />

        <Route
          path="about-us"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <AboutUs />
            </Suspense>
          }
        />

        <Route
          path="buyer-vendor-protection"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <BuyerAndVendorProtection />
            </Suspense>
          }
        />

        <Route
          path="marketplace-guidelines"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <MarketplaceGuidelines />
            </Suspense>
          }
        />

        <Route
          path="refunds-returns"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <RefundsAndReturns />
            </Suspense>
          }
        />

        <Route
          path="buying-guide"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <BuyingGuide />
            </Suspense>
          }
        />

        <Route
          path="vendor-products/:vendorId"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <VendorAllProduct />
            </Suspense>
          }
        />

        <Route
          path="booking-terms"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <BookingTerms />
            </Suspense>
          }
        />

        <Route
          path="subscription-terms"
          element={
            <Suspense fallback={<LandingPageSkeleton />}>
              <SubscriptionTerms />
            </Suspense>
          }
        />

        <Route path=":category/*" element={<PageNotFound />} />
        <Route path="*" element={<PageNotFound />} />
      </Route>

      <Route
        path="cart-items"
        element={
          <Suspense fallback={<CartItemsSkeleton />}>
            <CartItems />
          </Suspense>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route
          path="cart-items/checkout"
          element={
            <Suspense fallback={<CheckoutItemsSkeleton />}>
              <CheckoutItems />
            </Suspense>
          }
        />
        <Route
          path="cart-items/checkout/mandate/create/"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <DirectDebitSetup />
            </Suspense>
          }
        />
        <Route
          path="cart-items/checkout/direct-debit/success"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <DirectDebitInitialized />
            </Suspense>
          }
        />
        <Route
          path="verification-document-sent"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <VerificationSent />
            </Suspense>
          }
        />

        <Route
          path="cart-items/checkout/payment-success/:orderId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <CheckoutPaymentSuccess />
            </Suspense>
          }
        />
        <Route
          path="cart-items/checkout/payment-failed/:orderId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <CheckoutPaymentFailed />
            </Suspense>
          }
        />

        {/* StaySmall */}
        <Route
          path="/staySmallSmall/checkout/payment-success/:bookingID/:totalAmount"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingReceiptSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/modify-stay/payment-success/:bookingID/:totalAmount"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingReceiptSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/extend-stay/payment-success/:bookingID/:totalAmount"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingReceiptSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/checkout/booking-initiated/:bookingID"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingInitiatedSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/extend-stay/booking-initiated/:bookingID"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingInitiatedSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/modify-stay/booking-initiated/:bookingID"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <StaySmallSmallBookingInitiatedSuccess />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/extend-stay/payment/:bookingID/:newCheckOutDate/:amount"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <ExtendStayPayment />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/modify-stay/payment/:bookingID/:newCheckOutDate/:amount"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <ExtendStayPayment />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/extend-stay/success/:stayId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <ExtendStaySuccessReceipt />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/modify-stay/success/:stayId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <ModifyStaySuccessReceipt />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/modify-stay-refund/success/:bookingID"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <ModifyStayRefundSuccessReceipt />
            </Suspense>
          }
        />

        <Route
          path="/staySmallSmall/current-stay/cancel-stay/success/:stayId"
          element={
            <Suspense fallback={<CheckoutPaymentSkeleton />}>
              <CancelStaySuccessReceipt />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/sign-up"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <SignUp />
          </Suspense>
        }
      />
      <Route
        path="/sign-up/verify-email/:email"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <VerifyEmail />
          </Suspense>
        }
      />

      <Route
        path="/login"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <Login />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <ForgotPassword />
          </Suspense>
        }
      />
      <Route
        path="/reset-password/:email"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <ResetPassword />
          </Suspense>
        }
      />

      <Route path="*" element={<PageNotFound />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<UserDashboardLayout />}>
          <Route
            path="user-dashboard"
            element={
              <Suspense fallback={<DashboardSummarySkeleton />}>
                <UserDashboard />
              </Suspense>
            }
          >
            <Route
              path="shopping-overview"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <ShoppingOverview />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route
                path="summary"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Summary />
                  </Suspense>
                }
              />
              <Route
                path="purchases"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Purchases />
                  </Suspense>
                }
              />
              {/* <Route
                path="credit-wallet"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <CreditWallet />
                  </Suspense>
                }
              /> */}
              <Route
                path="favorites"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Favorites />
                  </Suspense>
                }
              />
              <Route
                path="direct-debit"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <DirectDebit />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RecentlyViewed />
                  </Suspense>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            {/* <Route
              path="notifications"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <Notifications />
                </Suspense>
              }
            /> */}

            {/* //Shortlet Profile Dashboard */}

            {/* <Route
              path="/user-dashboard/staySmallSmall"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <StaySmallSmallProfile />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route path="summary" element={<StaySmallSmallSummary />} />
              <Route path="stay" element={<StaySmallSmallStay />} />
              <Route path="favorites" element={<StaySmallSmallFavorites />} />
              <Route
                path="recently-viewed"
                element={<StaySmallSmallRecentlyViewed />}
              />
            </Route> */}

            {/* //Shortlet Profile Dashboard */}
            <Route
              path="/user-dashboard/staySmallSmall"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <StaySmallSmallProfile />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route
                path="summary"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <StaySmallSmallSummary />
                  </Suspense>
                }
              />
              <Route
                path="stay"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <StaySmallSmallStay />
                  </Suspense>
                }
              />
              <Route
                path="favorites"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <StaySmallSmallFavorites />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <StaySmallSmallRecentlyViewed />
                  </Suspense>
                }
              />
            </Route>

            {/* RentSmallSmall Dashboard */}
            <Route
              path="/user-dashboard/rentSmallSmall"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <RentSmallSmallProfile />
                </Suspense>
              }
            >
              <Route index element={<Navigate to="summary" replace />} />
              <Route
                path="summary"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallSummary />
                  </Suspense>
                }
              />
              <Route
                path="inspections"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallInspections />
                  </Suspense>
                }
              />
              <Route
                path="my-rental"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallRental />
                  </Suspense>
                }
              />
              <Route
                path="rental-verification"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallRentalVerification />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallRecentlyViewed />
                  </Suspense>
                }
              />
              <Route
                path="favorites"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RentSmallSmallFavorites />
                  </Suspense>
                }
              />
            </Route>

            {/* //Account Profile Dashboard */}
            <Route
              path="account-profile"
              element={
                <Suspense fallback={<DashboardSummarySkeleton />}>
                  <UserAccountProfile />
                </Suspense>
              }
            >
              <Route
                index
                element={<Navigate to="profile-summary" replace />}
              />
              <Route
                path="profile-summary"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <ProfileSummary />
                  </Suspense>
                }
              />
              <Route
                path="account-verification"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <AccountVerification />
                  </Suspense>
                }
              />
              <Route
                path="delivery-address"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <DeliveryAddress />
                  </Suspense>
                }
              />
              <Route
                path="feedback"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <Feedback />
                  </Suspense>
                }
              />
              <Route
                path="resolution-centre"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <ResolutionCentre />
                  </Suspense>
                }
              />
              <Route
                path="recently-viewed"
                element={
                  <Suspense fallback={<DashboardSummarySkeleton />}>
                    <RecentlyViewed />
                  </Suspense>
                }
              />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route
              path=":id/slug"
              element={
                <Suspense fallback={<ProductDetailsSkeleton />}>
                  <SingleProductPage />
                </Suspense>
              }
            />

            <Route path="*" element={<PageNotFound />} />
          </Route>
          <Route
            path="complete-booking"
            element={
              <Suspense fallback={<LandingPageSkeleton />}>
                <CompleteBooking />
              </Suspense>
            }
          />
        </Route>
      </Route>

      <Route
        path="/real-estate/rent-smallsmall/subscriber-verification/:propertyId"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <SubscriberVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/returning-customer-verification/:propertyId"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <ReturningCustomerVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/verification-success"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <RSSSubscriberVerificationSuccessPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/payment-setup"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <RSSPaymentSetupPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/payment-success"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <RSSPaymentSuccessPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/prime/apply/:propertyId"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <PrimeVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/prime/returning-customer-verification/:propertyId"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <PrimeReturningCustomerVerificationPage />
          </Suspense>
        }
      />
      <Route
        path="/real-estate/rent-smallsmall/prime/verification-success"
        element={
          <Suspense fallback={<LandingPageSkeleton />}>
            <PrimeVerificationSuccessPage />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
